import { useContext, useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import yup from 'utils/yup';
import { getDays, getMonths, isValidDay, getBirthdayYearOptions } from 'utils/date';
import { diContainerContext } from 'application/contexts/useDiContainer';
import { AgentMemberQuery } from 'interfaceAdapter/queries/AgentMember';
import { AgentUserRepository } from 'interfaceAdapter/repositories/AgentUser';
import { useNavigate, useParams } from 'react-router-dom';
import { COMPANY_USER_ROLE } from 'domain/entities/CompanyUser/CompanyUser';
import { useLoading } from 'application/contexts/useLoading';

export const schema = yup.object().shape({
    id: yup.number().required(),
    first_name: yup.string().required().max(50),
    last_name: yup.string().required().max(50),
    first_name_kana: yup.string().required().max(50),
    last_name_kana: yup.string().required().max(50),
    icon_image: yup.string().nullable().defined(),
    department: yup.string().nullable().defined(),
    job: yup.string().nullable().defined(),
    gender: yup.number().required(),
    phone_number: yup.string().required().max(30).onlyNumber().defined(),
    year: yup.number().required(),
    month: yup.number().required(),
    day: yup.number().required(),
    role: yup.number().oneOf(Object.values(COMPANY_USER_ROLE)).required(),
});

type AgentUserForm = yup.InferType<typeof schema>;

export const useMemberEdit = () => {
    const diContainer = useContext(diContainerContext);
    const agentMemberQuery = diContainer.resolve(AgentMemberQuery);
    const agentUserRepository = diContainer.resolve(AgentUserRepository);
    const { loading, setLoading } = useLoading();
    const [showSnackbar, setShowSnackbar] = useState(false);
    const navigate = useNavigate();
    const params = useParams();

    const {
        control,
        handleSubmit,
        formState: { errors },
        getValues,
        watch,
        setValue,
        setError,
        clearErrors,
    } = useForm<AgentUserForm>({
        resolver: yupResolver(schema),
        mode: 'onChange',
        defaultValues: {
            icon_image: null,
            year: 1920,
            month: 1,
            day: 1,
            job: '',
            department: '',
        },
    });

    const submit = handleSubmit(async (data) => {
        setLoading(true);
        try {
            const birthday = `${data.year}-${data.month}-${data.day}`;

            await agentUserRepository.editProfile({
                ...data,
                birthday,
            });
            setShowSnackbar(true);
            setTimeout(() => window.location.replace('/agent/member/'), 1500);
        } finally {
            setLoading(false);
        }
    });

    useEffect(() => {
        // 生年月日の組み合わせに対するバリデーション
        const subscription = watch((value, { name }) => {
            if (!['year', 'month', 'day'].includes(name ?? '')) {
                return;
            }
            const { year = -1, month = -1, day = -1 } = value;
            if (value.year === -1 || value.month === -1 || value.day === -1) {
                return;
            }
            if (!isValidDay(year, month, day)) {
                // 便宜上yearのエラーとして登録
                setError('year', { message: '正しい日付を入力してください。' });
            } else {
                clearErrors('year');
            }
        });

        const fetchMember = async () => {
            if (params.user_id === undefined) return;
            const member = await agentMemberQuery.fetchUser(Number(params.user_id));

            setValue('id', member.id);
            setValue('first_name', member.first_name);
            setValue('last_name', member.last_name);
            setValue('first_name_kana', member.first_name_kana ?? '');
            setValue('last_name_kana', member.last_name_kana ?? '');
            setValue('icon_image', member.icon_image);
            setValue('department', member.department ?? '');
            setValue('job', member.job ?? '');
            setValue('gender', member.gender);
            setValue('phone_number', member.phone_number ?? '');
            setValue('role', member.role);

            const birthday = member?.birthday?.split('-');
            setValue('year', Number(birthday?.[0]) ?? 1920);
            setValue('month', Number(birthday?.[1]) ?? 1);
            setValue('day', Number(birthday?.[2]) ?? 1);
        };

        subscription.unsubscribe();
        fetchMember();
    }, []);

    const yearOptions = getBirthdayYearOptions();
    const monthOptions = getMonths();
    const dayOptions = getDays();

    return {
        control,
        errors,
        loading,
        showSnackbar,
        yearOptions,
        monthOptions,
        dayOptions,
        setShowSnackbar,
        submit,
        getValues,
        setValue,
        watch,
    };
};
